import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Code, ConnectError } from '@connectrpc/connect'

import ConnectionCard from '@components/connection-card/ConnectionCard'
import LabelAndTextInput from '@components/inputs/LabelAndTextInput'
import Button from '@components/buttons/Button'
import padlock from '@assets/form/padlock.svg'
import { useValidatorContext } from '@/utils'
import { minLengthValidator, requiredValidator } from '@/utils/validators'
import { useBrokers } from '@/providers/brokerProvider'

// Mirrors minPasswordLength on the back-end, which refuses anything shorter.
const MIN_PASSWORD_LENGTH = 8

type Feedback = { text: string; kind: 'error' | 'notice' }

const noFeedback: Feedback = { text: '', kind: 'error' }

// resetError explains what went wrong in terms the broker can act on. A spent
// or expired link is the common case and needs its own way out.
function resetError(error: unknown): string {
	switch (ConnectError.from(error).code) {
		case Code.PermissionDenied:
			return 'Ce lien de réinitialisation a expiré ou a déjà été utilisé. Demandez-en un nouveau.'
		case Code.InvalidArgument:
			return `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères et les deux saisies doivent être identiques.`
		default:
			return 'Une erreur est survenue lors de la réinitialisation. Réessayez dans quelques instants.'
	}
}

// FormAdminPasswordReset is where the emailed link lands: it carries the
// single-use token in the path and trades it for a new password.
function FormAdminPasswordReset() {
	const { token = '' } = useParams<{ token: string }>()
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [feedback, setFeedback] = useState<Feedback>(noFeedback)
	const [submitting, setSubmitting] = useState(false)
	const [done, setDone] = useState(false)
	const brokers = useBrokers()
	const validator = useValidatorContext()
	const navigate = useNavigate()
	const formRef = useRef<HTMLFormElement>(null)

	// Submit through the form so the browser sees a real submit event and offers
	// to save the new password.
	const submitForm = () => formRef.current?.requestSubmit()

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			event.preventDefault()
			submitForm()
		}
	}

	const handleSubmit = async () => {
		setFeedback(noFeedback)
		if (!validator.validate()) return

		// Checked here as well as by the field validator so the mismatch is named
		// rather than reported as a generic invalid argument by the back-end.
		if (password !== confirmPassword) {
			setFeedback({ text: 'Les deux mots de passe ne sont pas identiques.', kind: 'error' })
			return
		}

		setSubmitting(true)
		try {
			await brokers.client.resetPassword({ token, password, confirmPassword })
			setDone(true)
		} catch (error) {
			console.error('Password reset error: ', error)
			setFeedback({ text: resetError(error), kind: 'error' })
		} finally {
			setSubmitting(false)
		}
	}

	// A link with no token cannot be completed, so say so instead of presenting a
	// form that can only fail.
	if (token === '') {
		return (
			<ConnectionCard>
				<p className='connectionError'>Ce lien de réinitialisation est incomplet.</p>
				<div className='connectionFooter'>
					<button
						type='button'
						className='connectionFooterLink'
						onClick={() => navigate('/form-admin-password-reset')}
					>
						Demander un nouveau lien
					</button>
				</div>
			</ConnectionCard>
		)
	}

	if (done) {
		return (
			<ConnectionCard>
				<p className='connectionNotice'>
					Votre mot de passe a bien été modifié. Vous pouvez maintenant vous connecter.
				</p>
				<div className='connectionFooter'>
					<button
						type='button'
						className='connectionFooterLink'
						onClick={() => navigate('/form-admin-connection')}
					>
						Aller à la connexion
					</button>
				</div>
			</ConnectionCard>
		)
	}

	return (
		<ConnectionCard>
			<form
				ref={formRef}
				autoComplete='on'
				onKeyDown={handleKeyDown}
				onSubmit={event => {
					event.preventDefault()
					handleSubmit()
				}}
			>
				<p className='connectionIntro'>Choisissez votre nouveau mot de passe.</p>
				<LabelAndTextInput
					id='password'
					name='password'
					type='password'
					label='Nouveau mot de passe'
					className='connectionField'
					hideLabel={true}
					icon={<img src={padlock} alt='' />}
					autocomplete='new-password'
					placeholder='Nouveau mot de passe'
					value={password}
					onChange={setPassword}
					validator={(input: string) => [
						requiredValidator(input),
						minLengthValidator(input, MIN_PASSWORD_LENGTH),
					]}
				/>
				<LabelAndTextInput
					id='confirmPassword'
					name='confirmPassword'
					type='password'
					label='Confirmer le mot de passe'
					className='connectionField'
					hideLabel={true}
					icon={<img src={padlock} alt='' />}
					autocomplete='new-password'
					placeholder='Confirmer le mot de passe'
					value={confirmPassword}
					onChange={setConfirmPassword}
					validator={(input: string) => [requiredValidator(input)]}
				/>
				<Button id='send' onClick={submitForm} loading={submitting}>
					Valider
				</Button>
				{feedback.text !== '' && (
					<p className={feedback.kind === 'error' ? 'connectionError' : 'connectionNotice'}>
						{feedback.text}
					</p>
				)}
				<div className='connectionFooter'>
					<button
						type='button'
						className='connectionFooterLink'
						onClick={() => navigate('/form-admin-connection')}
					>
						Retour à la connexion
					</button>
				</div>
			</form>
		</ConnectionCard>
	)
}

export default FormAdminPasswordReset
